import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { FaBoxOpen, FaSearch } from 'react-icons/fa';

const STATUS_STYLES = {
    pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    'out for delivery': 'bg-blue-100 text-blue-700 border-blue-200',
    delivered: 'bg-green-100 text-green-700 border-green-200',
    cancelled: 'bg-red-100 text-red-700 border-red-200'
};

const FranchiseOrders = ({ franchiseId }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [franchiseId]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await apiClient.get('/franchise/my-orders', {
                params: franchiseId ? { franchiseId } : {},
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(response.data);
        } catch (error) {
            console.error('Failed to fetch franchise orders:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="p-8 text-center bg-white rounded-lg shadow">Loading orders...</div>;

    const filteredOrders = orders.filter(order =>
        (order.orderNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.customerInfo?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.shippingAddress?.postalCode || '').includes(searchTerm)
    );

    return (
        <div className="space-y-4">
            <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="Search order number, customer, pincode..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none w-full md:w-96 shadow-sm"
                />
            </div>

            {filteredOrders.length === 0 ? (
                <div className="bg-white p-12 text-center rounded-xl border-2 border-dashed border-gray-200">
                    <FaBoxOpen className="text-gray-300 text-4xl mx-auto mb-4" />
                    <h3 className="text-lg font-bold text-gray-600">No orders found</h3>
                    <p className="text-gray-400 text-sm">Orders placed within your covered pincodes will show up here.</p>
                </div>
            ) : (
                <div className="grid gap-4">
                    {filteredOrders.map(order => (
                        <div key={order._id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="bg-gray-50 border-b border-gray-100 px-5 py-3 flex flex-wrap items-center justify-between gap-2">
                                <div>
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Order</span>
                                    <p className="text-sm font-bold text-gray-900">{order.orderNumber}</p>
                                </div>
                                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border uppercase ${STATUS_STYLES[order.status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                    {order.status}
                                </span>
                                <div className="text-right">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total</span>
                                    <p className="text-sm font-bold text-green-600">₹{order.totalPrice}</p>
                                </div>
                            </div>
                            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs font-bold text-gray-400 uppercase mb-1">Customer</p>
                                    <p className="text-sm text-gray-800 font-semibold">{order.customerInfo?.name}</p>
                                    <p className="text-xs text-gray-500">{order.customerInfo?.phone}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {order.shippingAddress?.address}, {order.shippingAddress?.city} - {order.shippingAddress?.postalCode}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-gray-400 uppercase mb-1">Items</p>
                                    <div className="space-y-1">
                                        {order.orderItems.map((item, i) => (
                                            <p key={i} className="text-xs text-gray-700">
                                                {item.name} <span className="text-gray-400">({item.size})</span> × {item.qty}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FranchiseOrders;
